import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Icon from 'components/Icon';
import Seo from 'components/Seo';
import { blogAPI } from 'utils/api';

const CATS = ['All','Technology','Web Development','Python','Cyber Security','AI & ML','General'];

// Turn a category name into its CSS color class.
// "Web Development" -> "blog-cat-web-development", "AI & ML" -> "blog-cat-ai-ml"
function catClass(name) {
  if (!name) return 'blog-cat-default';
  const slug = name.toLowerCase().replace(/&/g, '').replace(/\s+/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '');
  const known = ['technology','web-development','python','cyber-security','ai-ml','general'];
  return known.includes(slug) ? `blog-cat-${slug}` : 'blog-cat-default';
}

export default function BlogPage() {
  const [blogs, setBlogs]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [cat, setCat]       = useState('All');
  const [page, setPage]     = useState(1);
  const [total, setTotal]   = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    setLoading(true);
    const p = { limit:9, page };
    if (cat !== 'All') p.category = cat;
    blogAPI.getAll(p)
      .then(r => { setBlogs(r.data.blogs); setTotal(r.data.total); setTotalPages(r.data.totalPages); })
      .catch(() => setBlogs([]))
      .finally(() => setLoading(false));
  }, [cat, page]);

  const changeCat = c => { setCat(c); setPage(1); };

  return (
    <>
    <Seo title="Blog — Tips & Guides" description="Guides and tips on PDFs, images, and free online tools." path="/blog" />
    <div style={{ minHeight:'100vh', background:'var(--white)' }}>
      {/* Hero */}
      <div style={{ background:'linear-gradient(160deg,var(--blue-50),#e8f0fe)', borderBottom:'1px solid var(--blue-100)', padding:'56px 0 48px' }}>
        <div className="container">
          <div style={{ display:'flex', alignItems:'center', gap:16, marginBottom:20 }}>
            <div style={{ width:48, height:48, background:'var(--blue-dd)', borderRadius:'var(--r)', display:'flex', alignItems:'center', justifyContent:'center', color:'white' }}>
              <Icon name="blog" size={24} />
            </div>
            <div>
              <h1 style={{ fontSize:'clamp(26px,4vw,38px)', fontWeight:800, letterSpacing:-1, color:'var(--blue-dd)' }}>Free Tools Blog</h1>
              <p style={{ color:'var(--slate-500)', fontSize:15, marginTop:2 }}>"Guides for exam forms, resumes & free PDF tools"</p>
            </div>
          </div>
          <div className="filters" style={{ marginBottom:0 }}>
            {CATS.map(c => (
              <button key={c} className={`filter-btn ${cat===c?'active':''}`} onClick={() => changeCat(c)}>{c}</button>
            ))}
          </div>
        </div>
      </div>

      <div style={{ padding:'48px 0 80px' }}>
        <div className="container">
          {!loading && <p style={{ fontSize:13, color:'var(--slate-400)', marginBottom:28, fontWeight:500 }}>{total} article{total!==1?'s':''} {cat!=='All'?`in ${cat}`:''}</p>}

          {loading ? (
            <div className="loading"><div className="spinner" />Loading articles...</div>
          ) : blogs.length === 0 ? (
            <div style={{ textAlign:'center', padding:'100px 20px' }}>
              <div style={{ width:56, height:56, background:'var(--slate-100)', borderRadius:'var(--r-lg)', display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 16px', color:'var(--slate-300)' }}>
                <Icon name="blog" size={26} />
              </div>
              <h3 style={{ fontSize:20, marginBottom:8 }}>No articles yet</h3>
              <p style={{ color:'var(--slate-400)' }}>No posts in this category.</p>
            </div>
          ) : (
            <>
              <div className="blog-grid">
                {blogs.map(b => (
                  <Link to={`/blog/${b.slug}`} key={b._id} className="blog-card">
                    <div className="blog-card-body">
                      <span className={`cat-badge ${catClass(b.category)}`}>{b.category}</span>
                      <h3>{b.title}</h3>
                      <p>{b.excerpt}</p>
                    </div>
                    <div className="blog-card-foot">
                      <span style={{ display:'flex', alignItems:'center', gap:5 }}><Icon name="calendar" size={12} />{new Date(b.createdAt).toLocaleDateString('en-US',{month:'short',day:'numeric',year:'numeric'})}</span>
                      <span className="read-more">Read <Icon name="arrowRight" size={13} /></span>
                    </div>
                  </Link>
                ))}
              </div>

              {totalPages > 1 && (
                <div style={{ display:'flex', justifyContent:'center', alignItems:'center', gap:10, marginTop:48 }}>
                  <button className="btn btn-secondary" disabled={page===1} onClick={() => setPage(p=>p-1)}>
                    <Icon name="arrowLeft" size={15} />Previous
                  </button>
                  <div style={{ display:'flex', gap:6 }}>
                    {Array.from({length:totalPages},(_,i)=>i+1).map(p => (
                      <button key={p} onClick={() => setPage(p)} style={{ width:36, height:36, borderRadius:'var(--r-sm)', border:p===page?'none':'1.5px solid var(--slate-200)', background:p===page?'var(--blue)':'var(--white)', color:p===page?'white':'var(--slate-600)', fontWeight:600, fontSize:14, cursor:'pointer', fontFamily:'var(--font)' }}>
                        {p}
                      </button>
                    ))}
                  </div>
                  <button className="btn btn-secondary" disabled={page===totalPages} onClick={() => setPage(p=>p+1)}>
                    Next <Icon name="arrowRight" size={15} />
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  </>
  );
}
